import { Box, Stack, Typography } from '@mui/material';

import { useUpdatePiecePagesOrderMutation } from '@api/piece';
import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, horizontalListSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Scrollbar } from '@components/Scrollbar';

import { useScreenOrientation } from '@hooks/useScreenOrientation';

import { IThumbnail, IThumbnailId } from '../../types';
import { Thumbnail } from './Thumbnail';

interface ThumbnailsListProps {
  thumbnails: IThumbnail[];
  active: IThumbnailId | null;
  pieceId: number;
  pieceDocumentId: number | null;
  handleClickThumbnail: (thumbnail: IThumbnailId) => any;
  onChangeThumbnails: (thumbnails: IThumbnail[]) => void;
  hideThumbnails?: boolean;
  showDndInfo?: boolean;
  showClickThumbnailInfo?: boolean;
}

export const ThumbnailsList = ({
  thumbnails,
  active,
  pieceId,
  pieceDocumentId,
  hideThumbnails,
  showDndInfo,
  showClickThumbnailInfo,
  handleClickThumbnail,
  onChangeThumbnails,
}: ThumbnailsListProps) => {
  const [updatePiecePagesOrder] = useUpdatePiecePagesOrderMutation();

  const orientation = useScreenOrientation();

  const reorder = (startIndex: number, endIndex: number) => {
    const result = Array.from(thumbnails);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    const reorderedThumbnails = result.map((thumbnail, index) => ({ ...thumbnail, order: index + 1 }));

    onChangeThumbnails(reorderedThumbnails);

    return reorderedThumbnails;
  };

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const onDragThumbnail = async (event: DragEndEvent) => {
    const { active: { id: activeId }, over } = event;

    if (!pieceDocumentId || !over || activeId === over.id) return;

    const oldIndex = thumbnails.findIndex((t) => t.id === activeId);
    const newIndex = thumbnails.findIndex((t) => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    try {
      const orderedThumbnails = reorder(oldIndex, newIndex);
      const ids = orderedThumbnails.map((t) => t.id) as number[];
      await updatePiecePagesOrder({ order: ids, pieceId }).unwrap();
    } catch (error) {
      console.error(error);
      reorder(newIndex, oldIndex);
    }
  };

  const correctThumbnails = thumbnails.sort((a, b) => a.order - b.order);
  const tempThumbnails = correctThumbnails.filter((thumbnail) => typeof thumbnail.id === 'string');
  const isDndDisabled = !!tempThumbnails.length;

  return (
    <Scrollbar sx={{ height: '100%' }}>
      <Stack
        direction={orientation === 'landscape' ? 'column' : 'row'}
        spacing={2}
        sx={{
          display: 'inline-flex',
          height: orientation === 'landscape' ? 'auto' : '100%',
          alignItems: 'center',
          p: 2,
        }}
      >
        {showClickThumbnailInfo && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: 'white' }} variant="body2">
              Document Contents
            </Typography>
            <Typography sx={{ color: 'text.secondary' }} variant="subtitle2">
              Click image to edit.
            </Typography>
          </Box>
        )}
        <DndContext sensors={sensors} onDragEnd={onDragThumbnail} modifiers={[restrictToParentElement]}>
          <SortableContext
            items={correctThumbnails.map((t) => t.id)}
            strategy={orientation === 'landscape' ? verticalListSortingStrategy : horizontalListSortingStrategy}
          >
            <Stack direction={orientation === 'landscape' ? 'column' : 'row'} spacing={2} sx={{ height: '100%' }}>
              {!hideThumbnails &&
                correctThumbnails.map((thumbnail, index) => {
                  const { id } = thumbnail;
                  const isActive = active === id;
                  return (
                    <Thumbnail
                      {...thumbnail}
                      isActive={isActive}
                      index={index}
                      key={id}
                      onClickThumbnail={() => handleClickThumbnail(thumbnail.id)}
                      isDragDisabled={isDndDisabled}
                    />
                  );
                })}
            </Stack>
          </SortableContext>
        </DndContext>
        {showDndInfo && !isDndDisabled && (
          <Typography sx={{ minWidth: '70px', color: 'text.secondary', textAlign: 'center' }} variant="subtitle2">
            Drag and drop items to reorder
          </Typography>
        )}
      </Stack>
    </Scrollbar>
  );
};
